import { ReactElement, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import PrivateLayout from '@/layouts/PrivateLayout';
import { Helmet } from 'react-helmet-async';
import MainContentsLayout from '@/layouts/MainContentsLayout';
import PanelContents from '@/layouts/panelContents/PanelContents';
import Search from '@/features/analysis/seach/Search';
import { AnalysisDayPaginationBar } from '@/features/analysis/dayPagination/AnalysisDayPaginationBar';
import ScoreCard from '@/ui/scoreCard/ScoreCard';
import Ranking from '@/ui/Ranking';
import DonutChart from '@/features/charts/donutChart/DonutChart';
import NormalVerticalBarChart from '@/features/charts/normalVerticalBarChart/NormalvVerticalBarChart';
import StackedVerticalBarChart from '@/features/charts/stackedVerticalBarChart/StackedVerticalBarChart';
import HoverMenuButton from '@/components/menu/HoverMenuButton';
import type {
    RatioEvents,
    RatioDailyAggregatedItem,
    TransitionEventItems,
} from '@/types/AnalysisResultType';
import { useUser } from '@/hooks/UserContext';
import { usePreferredTimeZone } from '@/hooks/usePreferredTimeZone';
import FirstTimeExperienceManager from '@/features/firstTimeExperience/FirstTimeExperienceManager';
import { apiGetRequest } from '@/utils/apiClient';
import { buildPlansUrlWithTracking } from '@/utils/openPlansWithTracking';
import CalendarScopeErrorModal from '@/components/CalendarScopeErrorModal';
import { convertTimeForScorecard } from '@/ui/scoreCard/util/ScoreCard';
import Alert from '@/components/Alert';
import Table from '@/features/table/Table';
import Loading from '@/components/Loading';
import FeedbackForm from '@/features/feedbackForm/FeedbackForm';
import Advertisement from '@/features/advertisement/Advertisement';
import { useTranslation } from 'react-i18next';
import {
    getWeekday,
    removeYearFromDate,
    removeZeroPaddingFromDate,
    getHoursAndMinutes,
} from '@/utils/time';
import { getColors } from '@/services/colorService';
import type {
    StackedVerticalBarChartData,
    StackedVerticalBarChartDataset,
    StackedVerticalBarChartTooltipLabelFormatter,
    StackedVerticalBarChartTooltipFooterFormatter,
} from '@/features/charts/stackedVerticalBarChart/util/StackedVerticalBarChart';
import {
    IndividualCalendarItem,
    isHolidayOrBirthdayCalendar,
} from '@/features/analysis/seach/util/Search';
import { formatDateForServer } from '@/features/analysis/seach/util/Search';
import { resolveWeekStart } from '@/i18n/weekStart';
import {
    aggregateDailySlice,
    applyPreviousPeriodRatioComparisons,
    type ClientFilterParams,
} from '@/features/analysis/dayPagination/aggregateDailyData';
import {
    eachInclusiveLocalDateStr,
    getCurrentPeriod,
    getMonthFetchRange,
    getMonthKeysInRange,
    getMonthPeriodContainingDate,
    getNextPeriod,
    getPeriodContainingDate,
    getAnalysisPrevPeriod,
    getPrevPeriod,
    inclusiveLocalCalendarDayCount,
    prefetchAnalysisWarmupRanges,
    startOfLocalDay,
    toDateStr,
    type PaginationBucket,
    type PeriodRange,
} from '@/features/analysis/dayPagination/periodUtils';
import {
    FREE_ANALYSIS_PERIOD_OUT_OF_RANGE_CODE,
    FreeAnalysisPeriodRejectedError,
    isPeriodWithinFreeAnalysisWindow,
} from '@/features/analysis/dayPagination/freeAnalysisPeriodWindow';
import { fetchAnalysisSearchDailyAggregated, groupDailyItemsByMonthKey } from '@/features/analysis/api/analysisSearchApi';
import {
    ANALYSIS_DISPLAY_MAX_SCHEDULES_PAID,
    getAnalysisDisplayScheduleLimit,
    SCHEDULE_DISPLAY_LIMIT_EXCEEDED_CODE,
} from '@/features/analysis/analysisDisplayLimits';
import { calculatePeriodComparison } from '@/utils/periodComparison';
import { logCustomAnalyticsEvent } from '@/utils/customAnalysisEventLog';

// ---------------------------------------------------------------------------
// 型
// ---------------------------------------------------------------------------

/** 月単位のキャッシュ */
type MonthlyCache = Map<string, RatioDailyAggregatedItem[]>;

/** YYYY-MM-DD をローカル暦のその日 0:00 に変換 */
function ymdToLocalDay(ymd: string): Date {
    const [y, m, d] = ymd.split('-').map(Number);
    return startOfLocalDay(new Date(y, m - 1, d));
}

const EMPTY_RATIO_EVENTS: RatioEvents = {
    ratioEventItems: [],
    totalTime: 0,
    averageTime: 0,
    totalCount: 0,
    dailyAggregatedData: [],
};

// ---------------------------------------------------------------------------
// コンポーネント
// ---------------------------------------------------------------------------

/**
 * 分析ページ（日次キャッシュ＋日/週/カスタムビュー・クライアント集計）
 */
function Analysis(): ReactElement {
    const { userInfo, isLoading: userLoading } = useUser();
    const preferredTimeZone = usePreferredTimeZone();
    const { t, i18n } = useTranslation('analysis');
    const { t: tCommon } = useTranslation('common');
    const weekStart = resolveWeekStart(userInfo?.weekStart);

    // -------------------------------------------------------------------------
    // キャッシュ＆期間ステート
    // -------------------------------------------------------------------------
    const cacheRef = useRef<MonthlyCache>(new Map());
    /** 調査ログ: 同一描画処理内の「最終」 POST /api/analysis/search のヘッダ相関のみ保持（本文・予定は保持しない）。 */
    const lastAnalysisApiServerTraceRef = useRef<string | null>(null);
    const lastAnalysisApiClientTraceRef = useRef<string | null>(null);
    const [bucket, setBucket] = useState<PaginationBucket>('week');
    const bucketRef = useRef<PaginationBucket>(bucket);
    const [period, setPeriod] = useState<PeriodRange>(() => getCurrentPeriod('week', weekStart));
    /** 初回ウォームアップ完了までフルスクリーンローディング（カレンダー切替では true のままにして Search を維持） */
    const [analysisFirstPaintDone, setAnalysisFirstPaintDone] = useState(false);
    /** カレンダー切替を含むウォームアップ取得中（集計は完了まで待つ） */
    const [isWarmupLoading, setIsWarmupLoading] = useState(false);
    const [isFetchingMore, setIsFetchingMore] = useState(false);

    // -------------------------------------------------------------------------
    // カレンダー
    // -------------------------------------------------------------------------
    const [calendarList, setCalendarList] = useState<IndividualCalendarItem[]>([]);
    const [activeCalendarId, setActiveCalendarId] = useState<number | null>(null);

    useEffect(() => {
        bucketRef.current = bucket;
    }, [bucket]);

    const defaultCalendar = useMemo(() => {
        const filtered = calendarList.filter((c) => !isHolidayOrBirthdayCalendar(c));
        return filtered.find((c) => c.isDefault) ?? filtered[0] ?? null;
    }, [calendarList]);

    // デフォルトカレンダーが取得できたら activeCalendarId をセット
    useEffect(() => {
        if (defaultCalendar && activeCalendarId === null) {
            setActiveCalendarId(defaultCalendar.id);
        }
    }, [defaultCalendar, activeCalendarId]);

    // -------------------------------------------------------------------------
    // 検索フィルタ（キーワード・色 — クライアント側で使う）
    // -------------------------------------------------------------------------
    const [filterKeyword, setFilterKeyword] = useState('');
    const [filterColor, setFilterColor] = useState('');
    const filterColorRef = useRef(filterColor);

    // -------------------------------------------------------------------------
    // 表示用集計結果
    // -------------------------------------------------------------------------
    const [ratioEvents, setRatioEvents] = useState<RatioEvents>({
        ratioEventItems: [],
        totalTime: 0,
        averageTime: 0,
        totalCount: 0,
        dailyAggregatedData: [],
    });
    const [errorMessage, setErrorMessage] = useState<string[]>([]);
    const [freeAnalysisPeriodLocked, setFreeAnalysisPeriodLocked] = useState(false);
    /** 選択期間・条件のクライアント集計結果がプラン別の「1 画面あたりスケジュール上限」を超えたとき */
    const [scheduleDisplayLimitLocked, setScheduleDisplayLimitLocked] = useState(false);

    useEffect(() => {
        if (userInfo?.isPaid) {
            setFreeAnalysisPeriodLocked(false);
        }
    }, [userInfo?.isPaid]);

    // -------------------------------------------------------------------------
    // API フェッチ
    // -------------------------------------------------------------------------

    /** 指定範囲を API から取得し、キャッシュに保存 → allDailyData を再構築 */
    const fetchAndCache = useCallback(
        async (calendarId: number, start: Date, end: Date, color: string = '') => {
            const tzHeaders =
                preferredTimeZone !== undefined ? { 'X-User-TimeZone': preferredTimeZone } : undefined;
            const fetchResult = await fetchAnalysisSearchDailyAggregated(
                {
                    calendarId,
                    startTime: formatDateForServer(start),
                    endTime: formatDateForServer(end),
                    searchKeywords: [],
                    color,
                },
                tzHeaders,
            );
            if (fetchResult === null) {
                return;
            }
            lastAnalysisApiServerTraceRef.current = fetchResult.serverTraceId;
            lastAnalysisApiClientTraceRef.current = fetchResult.clientTraceIdUsed;

            const dailyItems = fetchResult.dailyItems;
            const cache = cacheRef.current;
            const byMonth = groupDailyItemsByMonthKey(dailyItems);
            const spanMonths = getMonthKeysInRange(startOfLocalDay(start), startOfLocalDay(end));
            for (const mk of spanMonths) {
                cache.set(mk, byMonth.get(mk) ?? []);
            }
        },
        [preferredTimeZone],
    );

    // -------------------------------------------------------------------------
    // 初回データ取得（カレンダーリスト → 3ヶ月分フェッチ）
    // -------------------------------------------------------------------------
    useEffect(() => {
        const init = async () => {
            try {
                const res = await apiGetRequest('/api/settings/calendars');
                if (!res.ok) {
                    setAnalysisFirstPaintDone(true);
                    return;
                }
                const data = await res.json();
                const calendars: IndividualCalendarItem[] = data.calendars || [];
                setCalendarList(calendars);
                if (calendars.length === 0) {
                    setAnalysisFirstPaintDone(true);
                }
            } catch (error) {
                console.error('Error fetching calendars:', error);
                setAnalysisFirstPaintDone(true);
            }
        };
        void init();
    }, []);

    /** 利用可能カレンダーがなくウォームアップが走らない場合でも画面を開ける */
    useEffect(() => {
        if (userLoading) return;
        if (calendarList.length === 0) return;
        const usable = calendarList.filter((c) => !isHolidayOrBirthdayCalendar(c));
        if (usable.length === 0) {
            setAnalysisFirstPaintDone(true);
        }
    }, [calendarList, userLoading]);

    useEffect(() => {
        if (!activeCalendarId || userLoading) return;

        const color = filterColorRef.current;
        let cancelled = false;
        const load = async () => {
            setIsWarmupLoading(true);
            setErrorMessage([]);
            try {
                cacheRef.current = new Map();
                await prefetchAnalysisWarmupRanges(Boolean(userInfo?.isPaid), (start, end) =>
                    fetchAndCache(activeCalendarId, start, end, color),
                );
            } catch (e) {
                console.error('Initial fetch error:', e);
                if (e instanceof FreeAnalysisPeriodRejectedError) {
                    setFreeAnalysisPeriodLocked(true);
                    setRatioEvents(EMPTY_RATIO_EVENTS);
                } else {
                    setErrorMessage([t('clientData.loadFailed')]);
                }
            } finally {
                if (!cancelled) {
                    setIsWarmupLoading(false);
                    setAnalysisFirstPaintDone(true);
                }
            }
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, [activeCalendarId, fetchAndCache, t, userInfo?.isPaid, userLoading]);

    // -------------------------------------------------------------------------
    // 期間変更 → 必要なら追加フェッチ → 集計
    // -------------------------------------------------------------------------

    /** キャッシュに使った色パラメータ（色変更時はキャッシュ破棄＋再取得） */
    const cachedColorRef = useRef('');

    const ensureCacheAndAggregate = useCallback(
        async (prd: PeriodRange, kw: string, color: string) => {
            if (!activeCalendarId) return;

            const calendarIdForLog = activeCalendarId;

            const displayTraceId = crypto.randomUUID();
            const t0 = performance.now();
            const isDefaultCalendar =
                defaultCalendar != null && activeCalendarId === defaultCalendar.id;

            function emitAnalyticsDiag(payload: {
                cachedDailyRowCount: number;
                scheduleCount: number;
                ratioEventItemsCount: number;
                analysisResult: 'success' | 'empty' | 'error';
                execTimeMs: number;
                errorCode: string | null;
                missingMonthsFetchedCount: number | null;
            }): void {
                void logCustomAnalyticsEvent({
                    displayTraceId,
                    lastApiServerTraceId: lastAnalysisApiServerTraceRef.current,
                    lastApiClientTraceIdUsed: lastAnalysisApiClientTraceRef.current,
                    activeCalendarId: calendarIdForLog,
                    isDefaultCalendar,
                    viewBucket: bucket,
                    periodStart: toDateStr(prd.start),
                    periodEnd: toDateStr(prd.end),
                    filterColor: color,
                    hasKeyword: kw.trim().length > 0,
                    cachedDailyRowCount: payload.cachedDailyRowCount,
                    scheduleCount: payload.scheduleCount,
                    ratioEventItemsCount: payload.ratioEventItemsCount,
                    analysisResult: payload.analysisResult,
                    execTimeMs: payload.execTimeMs,
                    errorCode: payload.errorCode,
                    missingMonthsFetchedCount: payload.missingMonthsFetchedCount,
                });
            };

            const isPaid = Boolean(userInfo?.isPaid);
            if (isPaid) {
                setFreeAnalysisPeriodLocked(false);
            } else {
                if (!isPeriodWithinFreeAnalysisWindow(prd, preferredTimeZone)) {
                    setFreeAnalysisPeriodLocked(true);
                    setScheduleDisplayLimitLocked(false);
                    setRatioEvents(EMPTY_RATIO_EVENTS);
                    emitAnalyticsDiag({
                        cachedDailyRowCount: 0,
                        scheduleCount: 0,
                        ratioEventItemsCount: 0,
                        analysisResult: 'error',
                        execTimeMs: Math.round(performance.now() - t0),
                        errorCode: FREE_ANALYSIS_PERIOD_OUT_OF_RANGE_CODE,
                        missingMonthsFetchedCount: null,
                    });
                    return;
                }
                setFreeAnalysisPeriodLocked(false);
            }

            // 色が変わったらキャッシュを破棄して全期間再取得
            if (color !== cachedColorRef.current) {
                cachedColorRef.current = color;
                cacheRef.current = new Map();
                setIsFetchingMore(true);
                try {
                    await prefetchAnalysisWarmupRanges(Boolean(userInfo?.isPaid), (start, end) =>
                        fetchAndCache(activeCalendarId, start, end, color),
                    );
                } catch (e) {
                    if (e instanceof FreeAnalysisPeriodRejectedError) {
                        setFreeAnalysisPeriodLocked(true);
                        setScheduleDisplayLimitLocked(false);
                        setRatioEvents(EMPTY_RATIO_EVENTS);
                    } else {
                        console.error('Color change re-fetch error:', e);
                    }
                } finally {
                    setIsFetchingMore(false);
                }
            }

            let neededMonths = getMonthKeysInRange(prd.start, prd.end);
            if (userInfo?.isPaid) {
                const prevRange = getAnalysisPrevPeriod(prd, bucket, weekStart);
                const prevMonths = getMonthKeysInRange(prevRange.start, prevRange.end);
                neededMonths = [...new Set([...neededMonths, ...prevMonths])];
            }
            const missing = neededMonths.filter((mk) => !cacheRef.current.has(mk));

            let missingMonthsFetchedCount: number | null = null;
            if (missing.length > 0) {
                setIsFetchingMore(true);
                missingMonthsFetchedCount = missing.length;
                try {
                    for (const mk of missing) {
                        const { start, end } = getMonthFetchRange(mk);
                        await fetchAndCache(activeCalendarId, start, end, color);
                    }
                } catch (e) {
                    if (e instanceof FreeAnalysisPeriodRejectedError) {
                        setFreeAnalysisPeriodLocked(true);
                        setScheduleDisplayLimitLocked(false);
                        setRatioEvents(EMPTY_RATIO_EVENTS);
                        emitAnalyticsDiag({
                            cachedDailyRowCount: 0,
                            scheduleCount: 0,
                            ratioEventItemsCount: 0,
                            analysisResult: 'error',
                            execTimeMs: Math.round(performance.now() - t0),
                            errorCode: FREE_ANALYSIS_PERIOD_OUT_OF_RANGE_CODE,
                            missingMonthsFetchedCount,
                        });
                        return;
                    }
                    console.error('Additional fetch error:', e);
                } finally {
                    setIsFetchingMore(false);
                }
            }

            const cache = cacheRef.current;
            const all: RatioDailyAggregatedItem[] = [];
            for (const items of cache.values()) {
                all.push(...items);
            }
            all.sort((a, b) => a.date.localeCompare(b.date));

            const filters: ClientFilterParams = { keyword: kw };
            let result = aggregateDailySlice(all, toDateStr(prd.start), toDateStr(prd.end), filters);
            if (userInfo?.isPaid) {
                const prevRange = getAnalysisPrevPeriod(prd, bucket, weekStart);
                const prevResult = aggregateDailySlice(
                    all,
                    toDateStr(prevRange.start),
                    toDateStr(prevRange.end),
                    filters,
                );
                result = applyPreviousPeriodRatioComparisons(result, prevResult);
            }

            const scheduleMax = getAnalysisDisplayScheduleLimit(isPaid);
            if (result.totalCount > scheduleMax) {
                setScheduleDisplayLimitLocked(true);
                setRatioEvents(EMPTY_RATIO_EVENTS);
                const blockedItemLen = result.ratioEventItems?.length ?? 0;
                const execBlocked = Math.round(performance.now() - t0);
                emitAnalyticsDiag({
                    cachedDailyRowCount: all.length,
                    scheduleCount: result.totalCount,
                    ratioEventItemsCount: blockedItemLen,
                    analysisResult: 'error',
                    execTimeMs: execBlocked,
                    errorCode: SCHEDULE_DISPLAY_LIMIT_EXCEEDED_CODE,
                    missingMonthsFetchedCount,
                });
                return;
            }

            setScheduleDisplayLimitLocked(false);
            setRatioEvents(result);

            const itemLen = result.ratioEventItems?.length ?? 0;
            const isEmptyDisplay = result.totalCount === 0 && itemLen === 0;
            const execTimeMs = Math.round(performance.now() - t0);
            emitAnalyticsDiag({
                cachedDailyRowCount: all.length,
                scheduleCount: result.totalCount,
                ratioEventItemsCount: itemLen,
                analysisResult: isEmptyDisplay ? 'empty' : 'success',
                execTimeMs,
                errorCode: null,
                missingMonthsFetchedCount,
            });
        },
        [
            activeCalendarId,
            bucket,
            defaultCalendar,
            fetchAndCache,
            preferredTimeZone,
            userInfo?.isPaid,
            weekStart,
        ],
    );

    // period / filter 変更時に再集計
    useEffect(() => {
        if (isWarmupLoading) return;
        filterColorRef.current = filterColor;
        ensureCacheAndAggregate(period, filterKeyword, filterColor);
    }, [period, filterKeyword, filterColor, isWarmupLoading, ensureCacheAndAggregate]);

    // -------------------------------------------------------------------------
    // ページネーションハンドラ
    // -------------------------------------------------------------------------

    const handlePrev = useCallback(() => {
        if (bucket === 'custom') return;
        setPeriod((prev) => getPrevPeriod(prev, bucket, weekStart));
    }, [bucket, weekStart]);

    const handleNext = useCallback(() => {
        if (bucket === 'custom') return;
        setPeriod((prev) => getNextPeriod(prev, bucket, weekStart));
    }, [bucket, weekStart]);

    const handleBucketChange = useCallback(
        (newBucket: PaginationBucket) => {
            if (newBucket === 'custom') {
                setBucket('custom');
                return;
            }
            const fromCustom = bucketRef.current === 'custom';
            setBucket(newBucket);
            setPeriod((prev) => {
                if (fromCustom) {
                    return newBucket === 'month'
                        ? getMonthPeriodContainingDate(prev.start)
                        : getPeriodContainingDate(prev.start, weekStart);
                }
                return getCurrentPeriod(newBucket, weekStart);
            });
        },
        [weekStart],
    );

    const handlePeriodSelect = useCallback((range: PeriodRange) => {
        setPeriod(range);
    }, []);

    // -------------------------------------------------------------------------
    // Search コンポーネントからのフィルタ保存
    // -------------------------------------------------------------------------

    const handleSaveFilters = useCallback(
        (
            searchCalendar: { value: string; label: string },
            _startTime: string,
            _endTime: string,
            searchKeyword: string,
            searchColor: { value: string; label: string },
            _bucket: 'day' | 'week' | 'month',
        ) => {
            setFilterKeyword(searchKeyword);
            setFilterColor(searchColor?.value || '');

            const newCalId = Number(searchCalendar.value);
            if (newCalId && newCalId !== activeCalendarId) {
                setActiveCalendarId(newCalId);
            }
        },
        [activeCalendarId],
    );

    // -------------------------------------------------------------------------
    // チャート用のフォーマッタ（既存ロジックを維持）
    // -------------------------------------------------------------------------

    const scorecardTimeUnits = useMemo(
        () => ({
            hourUnit: tCommon('time.hourUnit'),
            minuteUnit: tCommon('time.minuteUnit'),
        }),
        // t の参照は言語切替で変わらないことがあるため、locale を明示的に依存に含める
        [tCommon, i18n.language],
    );

    const untitledLabel = useMemo(() => t('chart.untitled'), [t]);
    const totalPerDayLabel = useMemo(() => t('chart.tooltip.totalPerDay'), [t]);
    const stackedNoDataLabel = useMemo(() => t('chart.noData'), [t]);

    const formatScorecardTime = (time: number) => convertTimeForScorecard(time, scorecardTimeUnits);

    const colorCacheRef = useRef<{ language: string; map: Map<string, string> } | null>(null);

    const formatStackedDuration = useCallback((minutes: number) => {
        const [hours, mins] = getHoursAndMinutes(minutes);
        const parts: string[] = [];
        if (hours > 0) parts.push(`${hours}h`);
        if (mins > 0 || parts.length === 0) parts.push(`${mins}m`);
        return parts.join('');
    }, []);

    const formatMonthAwareLabel = useCallback(
        (currentDate: string, previousDate?: string) => {
            if (!currentDate) return '';
            const curNorm = currentDate.includes('/') ? currentDate.replace(/\//g, '-') : currentDate;
            const prevNorm = previousDate
                ? previousDate.includes('/')
                    ? previousDate.replace(/\//g, '-')
                    : previousDate
                : undefined;
            const curMonth = curNorm.split('-')[1];
            const prevMonth = prevNorm?.split('-')[1];
            const trimmed = removeZeroPaddingFromDate(removeYearFromDate(curNorm));
            const [m, d] = trimmed.split('-');
            const base = !prevMonth || prevMonth !== curMonth ? `${m}/${d}` : d;
            const weekday = getWeekday(curNorm, i18n.language);
            if (!weekday) return base.replace(/-/g, '/');
            const isJa = i18n.language?.startsWith('ja');
            const shortWeekday = isJa ? weekday.slice(0, 1) : weekday.slice(0, 2);
            const display = base.replace(/-/g, '/');
            return `${display}(${shortWeekday})`;
        },
        [i18n.language],
    );

    const [ratioChartView, setRatioChartView] = useState<'stacked' | 'donut'>('stacked');
    const [legendOpen, setLegendOpen] = useState(true);
    const legendAnchorRef = useRef<HTMLDivElement | null>(null);

    const stackedChartData = useMemo<StackedVerticalBarChartData | null>(() => {
        const daily = ratioEvents.dailyAggregatedData ?? [];
        if (!daily.length) return null;

        const sortedDaily = [...daily].sort((a, b) => String(a.date).localeCompare(String(b.date)));
        const labels = sortedDaily.map((item, idx) =>
            formatMonthAwareLabel(item.date, idx > 0 ? sortedDaily[idx - 1].date : undefined),
        );
        const dayTotals = sortedDaily.map((item) => Number(item.totalTime ?? 0));
        const length = sortedDaily.length;

        const datasetMap = new Map<string, { dataset: StackedVerticalBarChartDataset; totalMinutes: number }>();
        sortedDaily.forEach((day, dayIndex) => {
            day.eventItems.forEach((event) => {
                const rawKey = event.title ?? '';
                if (!datasetMap.has(rawKey)) {
                    datasetMap.set(rawKey, {
                        dataset: {
                            id: rawKey,
                            label: rawKey.trim().length > 0 ? rawKey : untitledLabel,
                            values: Array(length).fill(0),
                        },
                        totalMinutes: 0,
                    });
                }
                const holder = datasetMap.get(rawKey)!;
                const minutes = Number(event.totalTime ?? 0);
                holder.dataset.values[dayIndex] = minutes;
                holder.totalMinutes += minutes;
            });
        });

        const datasets = Array.from(datasetMap.values())
            .filter((e) => e.totalMinutes > 0)
            .sort((a, b) => b.totalMinutes - a.totalMinutes)
            .map((e) => e.dataset);

        if (!datasets.length) return null;
        return { labels, totals: dayTotals, datasets };
    }, [formatMonthAwareLabel, ratioEvents.dailyAggregatedData, untitledLabel]);

    const resolveStackedColor = useCallback(
        async (label: string) => {
            const key = label?.trim();
            if (!key) return undefined;
            const currentLanguage = i18n.language;
            if (!colorCacheRef.current || colorCacheRef.current.language !== currentLanguage) {
                try {
                    const colorDefinitions = await getColors('all', currentLanguage);
                    const map = new Map<string, string>();
                    colorDefinitions.forEach((c) => {
                        if (c.label) map.set(c.label, c.hexColor);
                        if (c.originalLabel) map.set(c.originalLabel, c.hexColor);
                    });
                    colorCacheRef.current = { language: currentLanguage, map };
                } catch {
                    colorCacheRef.current = { language: currentLanguage, map: new Map() };
                }
            }
            return colorCacheRef.current?.map.get(key);
        },
        [i18n.language],
    );

    const stackedTooltipLabelFormatter = useCallback<StackedVerticalBarChartTooltipLabelFormatter>(
        ({ label, value }) => {
            const displayLabel = label?.trim().length ? label : untitledLabel;
            return `${displayLabel}: ${formatStackedDuration(Math.round(value))}`;
        },
        [formatStackedDuration, untitledLabel],
    );

    const stackedTooltipFooterFormatter = useCallback<StackedVerticalBarChartTooltipFooterFormatter>(
        ({ total }) => (total === undefined ? '' : `${totalPerDayLabel}: ${formatStackedDuration(total)}`),
        [formatStackedDuration, totalPerDayLabel],
    );

    const stackedYAxisSuggestedMax = useMemo(() => {
        if (!stackedChartData?.totals?.length) return undefined;
        const max = Math.max(...stackedChartData.totals);
        if (!Number.isFinite(max) || max <= 0) return undefined;
        return max * 1.1;
    }, [stackedChartData]);

    const stackedAxisTickFormatter = useCallback(
        (value: number) => formatStackedDuration(Math.round(value)),
        [formatStackedDuration],
    );

    /** キーワードまたは特定色指定時は遷移モード（日付ベースの表・グラフ） */
    const analysisUsesTransitionView = useMemo(() => {
        const hasKeyword = filterKeyword.trim().length > 0;
        const hasSpecificColor = filterColor !== '' && filterColor !== 'all';
        return hasKeyword || hasSpecificColor;
    }, [filterKeyword, filterColor]);

    const transitionTableRows = useMemo(() => {
        const daily = ratioEvents.dailyAggregatedData ?? [];
        return daily
            .filter((d) => d.totalTime > 0)
            .map((d) => ({ date: d.date, time: d.totalTime }));
    }, [ratioEvents.dailyAggregatedData]);

    /** 遷移グラフ用：表示期間の全日（データなしは 0 分） */
    const transitionChartRows = useMemo((): TransitionEventItems[] => {
        const daily = ratioEvents.dailyAggregatedData ?? [];
        const timeByDate = new Map(daily.map((d) => [d.date, d.totalTime] as const));
        const dates = eachInclusiveLocalDateStr(period.start, period.end);
        return dates.map((date) => ({ date, time: timeByDate.get(date) ?? 0 }));
    }, [period.start, period.end, ratioEvents.dailyAggregatedData]);

    /**
     * 遷移モード専用：前期棒データ・日別ランキング・活動日数系スコアカード用の前期比。
     * 前期スライスは1回だけ aggregate する。
     */
    const transitionViewDerived = useMemo(() => {
        if (!analysisUsesTransitionView) return null;

        const daily = ratioEvents.dailyAggregatedData ?? [];
        const totalTime = ratioEvents.totalTime;
        const filters: ClientFilterParams = { keyword: filterKeyword };

        let prevResult: RatioEvents | null = null;
        let previousChartRows: TransitionEventItems[] | undefined;

        if (userInfo?.isPaid) {
            const cache = cacheRef.current;
            const all: RatioDailyAggregatedItem[] = [];
            for (const items of cache.values()) {
                all.push(...items);
            }
            all.sort((a, b) => a.date.localeCompare(b.date));
            const prevRange = getAnalysisPrevPeriod(period, bucket, weekStart);
            prevResult = aggregateDailySlice(
                all,
                toDateStr(prevRange.start),
                toDateStr(prevRange.end),
                filters,
            );
            const prevDaily = prevResult.dailyAggregatedData ?? [];
            const timeByDate = new Map(prevDaily.map((d) => [d.date, d.totalTime] as const));
            const dates = eachInclusiveLocalDateStr(prevRange.start, prevRange.end);
            previousChartRows = dates.map((date) => ({ date, time: timeByDate.get(date) ?? 0 }));
        }

        const prevByDate = new Map(
            (prevResult?.dailyAggregatedData ?? []).map((d) => [d.date, d.totalTime] as const),
        );

        const activeDays = daily.filter((d) => d.totalTime > 0).length;
        const periodDays = Math.max(1, inclusiveLocalCalendarDayCount(period.start, period.end));
        const avgPerActiveDay = activeDays > 0 ? Math.round(totalTime / activeDays) : 0;
        const prevDaily = prevResult?.dailyAggregatedData ?? [];
        const prevActiveDays = prevDaily.filter((d) => d.totalTime > 0).length;
        const prevTotal = prevResult?.totalTime ?? 0;
        const prevAvgPerActiveDay = prevActiveDays > 0 ? Math.round(prevTotal / prevActiveDays) : 0;

        const hasPrev = Boolean(userInfo?.isPaid && prevResult);
        const activeDaysComparison = hasPrev
            ? calculatePeriodComparison(activeDays, prevActiveDays)
            : undefined;
        const avgPerActiveDayComparison = hasPrev
            ? calculatePeriodComparison(avgPerActiveDay, prevAvgPerActiveDay)
            : undefined;

        const periodStart = startOfLocalDay(period.start);
        const prevRangeForRank = getAnalysisPrevPeriod(period, bucket, weekStart);
        const prevStart = startOfLocalDay(prevRangeForRank.start);

        const topDays = [...daily]
            .filter((d) => d.totalTime > 0)
            .sort((a, b) => b.totalTime - a.totalTime)
            .slice(0, 3);

        const rankingItems =
            totalTime <= 0
                ? []
                : topDays.map((d) => {
                      const pct = Math.round((d.totalTime / totalTime) * 100);
                      let timeComparison: ReturnType<typeof calculatePeriodComparison> | undefined;
                      if (prevByDate.size > 0) {
                          const offsetDays = Math.round(
                              (ymdToLocalDay(d.date).getTime() - periodStart.getTime()) / 86400000,
                          );
                          const prevDay = new Date(prevStart);
                          prevDay.setDate(prevDay.getDate() + offsetDays);
                          const prevKey = toDateStr(prevDay);
                          const prevT = prevByDate.get(prevKey) ?? 0;
                          timeComparison = calculatePeriodComparison(d.totalTime, prevT);
                      }
                      return {
                          title: d.date,
                          percentage: pct,
                          totalTime: d.totalTime,
                          timeComparison,
                      };
                  });

        return {
            previousChartRows,
            rankingItems,
            activeDays,
            periodDays,
            avgPerActiveDay,
            activeDaysComparison,
            avgPerActiveDayComparison,
        };
    }, [
        analysisUsesTransitionView,
        ratioEvents,
        userInfo?.isPaid,
        bucket,
        period,
        weekStart,
        filterKeyword,
    ]);

    const analysisRankingItems = useMemo(() => {
        if (analysisUsesTransitionView) {
            return transitionViewDerived?.rankingItems ?? [];
        }
        const sorted = [...ratioEvents.ratioEventItems]
            .sort((a, b) => b.percentage - a.percentage)
            .slice(0, 3);
        return sorted.map((item) => ({
            title: item.title,
            percentage: item.percentage,
            totalTime: item.totalTime,
            timeComparison: item.totalTimeComparison,
        }));
    }, [analysisUsesTransitionView, transitionViewDerived, ratioEvents.ratioEventItems]);

    // -------------------------------------------------------------------------
    // パネルコンポーネントマップ（Search / Pagination は外に出ているので含まない）
    // -------------------------------------------------------------------------

    const componentMap = useMemo(() => {
        return {
            anTotalTime: (
                <ScoreCard
                    title={t('cards.totalTime')}
                    score={formatScorecardTime(ratioEvents.totalTime)}
                    comparison={ratioEvents.totalTimeComparison}
                    comparisonValueType="time"
                    emptyLabel={t('cards.noData')}
                />
            ),
            anAverageTime:
                analysisUsesTransitionView && transitionViewDerived ? (
                    <ScoreCard
                        title={t('cards.averageTimePerActiveDay')}
                        score={formatScorecardTime(transitionViewDerived.avgPerActiveDay)}
                        comparison={transitionViewDerived.avgPerActiveDayComparison}
                        comparisonValueType="time"
                        emptyLabel={t('cards.noData')}
                    />
                ) : (
                    <ScoreCard
                        title={t('cards.averageTime')}
                        score={formatScorecardTime(ratioEvents.averageTime)}
                        comparison={ratioEvents.averageTimeComparison}
                        comparisonValueType="time"
                        emptyLabel={t('cards.noData')}
                    />
                ),
            anTotalSchedule:
                analysisUsesTransitionView && transitionViewDerived ? (
                    <ScoreCard
                        title={t('cards.activeDays')}
                        score={[
                            { type: 'number', value: String(transitionViewDerived.activeDays) },
                            { type: 'unit', value: ' / ' },
                            { type: 'unit', value: String(transitionViewDerived.periodDays) },
                            { type: 'unit', value: t('cards.activeDaysPeriodSuffix') },
                        ]}
                        comparison={transitionViewDerived.activeDaysComparison}
                        comparisonValueType="number"
                        emptyLabel={t('cards.noData')}
                    />
                ) : (
                    <ScoreCard
                        title={t('cards.totalCount')}
                        score={[{ type: 'number', value: String(ratioEvents.totalCount) }]}
                        comparison={ratioEvents.totalCountComparison}
                        comparisonValueType="number"
                        emptyLabel={t('cards.noData')}
                    />
                ),
            anRanking: (
                <Ranking
                    rankingItems={analysisRankingItems}
                    showSharePercentage={!analysisUsesTransitionView}
                />
            ),
            anAd: <Advertisement />,
            anChart: (
                <div className="flex h-full w-full flex-col ps-2 pb-2 pe-2">
                    <div className="relative flex-1 min-h-[260px] w-full">
                        {analysisUsesTransitionView ? (
                            <NormalVerticalBarChart
                                data={transitionChartRows}
                                previousData={transitionViewDerived?.previousChartRows}
                            />
                        ) : (
                            <>
                                <div className="absolute top-0 right-0 z-[2]" ref={legendAnchorRef}>
                                    <HoverMenuButton
                                        ariaLabel={t('chart.legendColors')}
                                        items={(() => {
                                            const canShowLegend = ratioChartView === 'stacked';
                                            const legendIcon = (
                                                <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                                                    <rect x="4" y="6" width="4" height="4" fill="#9CA3AF" />
                                                    <rect x="4" y="14" width="4" height="4" fill="#D1D5DB" />
                                                    <rect x="10" y="6" width="10" height="4" fill="#6B7280" />
                                                    <rect x="10" y="14" width="10" height="4" fill="#6B7280" />
                                                </svg>
                                            );
                                            const donutIcon = (
                                                <svg
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    aria-hidden="true"
                                                >
                                                    <path d="M12 3a9 9 0 1 0 9 9h-9V3z" fill="#9CA3AF" />
                                                    <path d="M12 12h9A9 9 0 0 1 12 21v-9z" fill="#D1D5DB" />
                                                </svg>
                                            );
                                            const stackedIcon = (
                                                <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                                                    <rect x="3" y="14" width="4" height="7" fill="#4B5563" />
                                                    <rect x="10" y="10" width="4" height="11" fill="#9CA3AF" />
                                                    <rect x="17" y="6" width="4" height="15" fill="#D1D5DB" />
                                                </svg>
                                            );
                                            const items = [
                                                {
                                                    key: 'legend',
                                                    label: t('chart.legendColors'),
                                                    icon: legendIcon,
                                                    onClick: canShowLegend
                                                        ? () => setLegendOpen((v) => !v)
                                                        : undefined,
                                                    disabled: !canShowLegend,
                                                },
                                                {
                                                    key: 'toggle-view',
                                                    label:
                                                        ratioChartView === 'stacked'
                                                            ? t('chart.toggle.donut')
                                                            : t('chart.toggle.stacked'),
                                                    icon: ratioChartView === 'stacked' ? donutIcon : stackedIcon,
                                                    onClick:
                                                        ratioChartView === 'stacked'
                                                            ? () => {
                                                                  setLegendOpen(false);
                                                                  setRatioChartView('donut');
                                                              }
                                                            : () => {
                                                                  setRatioChartView('stacked');
                                                                  setLegendOpen(true);
                                                              },
                                                },
                                            ];
                                            return items.filter((it) => !it.disabled);
                                        })()}
                                    />
                                </div>
                                {ratioChartView === 'stacked' ? (
                                    <StackedVerticalBarChart
                                        data={stackedChartData ?? { labels: [], datasets: [] }}
                                        resolveColor={resolveStackedColor}
                                        tooltipLabelFormatter={stackedTooltipLabelFormatter}
                                        yAxisSuggestedMax={stackedYAxisSuggestedMax}
                                        formatYAxisTick={stackedAxisTickFormatter}
                                        noDataMessage={stackedNoDataLabel}
                                        hideSwitchButton={true}
                                        hideLegendButton={true}
                                        legendOpen={legendOpen}
                                        onToggleLegend={() => setLegendOpen((v) => !v)}
                                        legendAnchorRef={
                                            legendAnchorRef as unknown as React.RefObject<HTMLElement | null>
                                        }
                                    />
                                ) : (
                                    <DonutChart
                                        data={ratioEvents}
                                        hideSwitchButton={true}
                                        onSwitchToStacked={() => {
                                            setRatioChartView('stacked');
                                            setLegendOpen(true);
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>
            ),
            anTable: (
                <Table
                    key={analysisUsesTransitionView ? 'analysis-transition' : 'analysis-ratio'}
                    tableType={analysisUsesTransitionView ? 'transition' : 'ratio'}
                    value={
                        analysisUsesTransitionView
                            ? transitionTableRows
                            : ratioEvents.ratioEventItems
                    }
                />
            ),
        };
    }, [
        ratioEvents,
        analysisRankingItems,
        ratioChartView,
        stackedChartData,
        resolveStackedColor,
        stackedTooltipLabelFormatter,
        stackedTooltipFooterFormatter,
        stackedYAxisSuggestedMax,
        stackedAxisTickFormatter,
        stackedNoDataLabel,
        formatScorecardTime,
        legendOpen,
        t,
        analysisUsesTransitionView,
        transitionViewDerived,
        transitionTableRows,
        transitionChartRows,
    ]);

    /** 結果 0 件ときのヒントバー表示（フェッチ後・読み込み終了のみ） */
    const showEmptyResultHints = useMemo(() => {
        if (!analysisFirstPaintDone) return false;
        if (isWarmupLoading || isFetchingMore) return false;
        if (errorMessage.length > 0) return false;
        if (freeAnalysisPeriodLocked || scheduleDisplayLimitLocked) return false;
        const empty =
            ratioEvents.totalCount === 0 && (ratioEvents.ratioEventItems?.length ?? 0) === 0;
        return empty;
    }, [
        analysisFirstPaintDone,
        isWarmupLoading,
        isFetchingMore,
        errorMessage.length,
        freeAnalysisPeriodLocked,
        scheduleDisplayLimitLocked,
        ratioEvents.totalCount,
        ratioEvents.ratioEventItems?.length,
    ]);

    // -------------------------------------------------------------------------
    // レンダリング
    // -------------------------------------------------------------------------

    const analysisPlanUpsellModalVisible =
        freeAnalysisPeriodLocked || scheduleDisplayLimitLocked;
    const displayScheduleLimitMax = getAnalysisDisplayScheduleLimit(
        Boolean(userInfo?.isPaid),
    );
    const basicPlanScheduleCapFormatted = new Intl.NumberFormat(
        i18n.language.startsWith('ja') ? 'ja-JP' : 'en-US',
    ).format(ANALYSIS_DISPLAY_MAX_SCHEDULES_PAID);

    return (
        <FirstTimeExperienceManager userInfo={userInfo}>
            <CalendarScopeErrorModal />
            <Helmet>
                <title>{t('meta.title')}</title>
            </Helmet>

            {/* 初回のみフルロード。カレンダー切替後も firstPaint 済みのため Search は維持（再マウントで選択が戻るのを防ぐ） */}
            {userLoading || !analysisFirstPaintDone ? (
                <Loading />
            ) : (
                <PrivateLayout>
                    <MainContentsLayout>
                        {/* 上部コントロール：検索条件 ＆ ページネーション（ヘッダー領域） */}
                        <div
                            className="sticky top-0 z-30 shadow-md pb-6 sm:pb-6"
                            data-grid-key="anSearch"
                        >
                            <Search
                                handleSearch={handleSaveFilters}
                                calendarList={calendarList}
                                hidePeriod
                                hideSubmitButton
                                analysisToolbarMiddle={
                                    <AnalysisDayPaginationBar
                                        bucket={bucket}
                                        onBucketChange={handleBucketChange}
                                        periodStart={period.start}
                                        periodEnd={period.end}
                                        weekStart={weekStart}
                                        isPaid={Boolean(userInfo?.isPaid)}
                                        onPeriodSelect={handlePeriodSelect}
                                        onPrev={handlePrev}
                                        onNext={handleNext}
                                    />
                                }
                            />
                        </div>

                        <div className="flex-1 bg-white pt-4 pb-12">
                            {errorMessage.length > 0 && (
                                <div className="px-4 sm:px-6 lg:px-8">
                                    <Alert
                                        type="danger"
                                        messages={errorMessage}
                                        className="mb-6 p-4 text-center"
                                        allowHtml={true}
                                    />
                                </div>
                            )}
                            {showEmptyResultHints ? (
                                <div className="px-4 sm:px-6 lg:px-8 mt-12 mb-20 flex flex-col items-center justify-center">
                                    <h3 className="mb-2 text-xl font-bold text-gray-900">{t('emptyResultHints.headline')}</h3>
                                    <p className="mb-4 text-gray-500">{t('emptyResultHints.intro')}</p>
                                    <img
                                        src="/images/analysis/schedule_notfound.png"
                                        alt={t('emptyResultHints.illustrationAlt')}
                                        className="mx-auto mb-8 w-full max-w-40 object-contain"
                                        loading="lazy"
                                        decoding="async"
                                    />

                                    <div className="mx-auto flex w-full max-w-xs flex-col items-start">
                                        <h4 className="mb-6 w-full text-center text-base font-bold text-gray-900">
                                            {t('emptyResultHints.causesTitle')}
                                        </h4>
                                        <ul className="flex w-full flex-col gap-6">
                                            <li className="flex w-full items-start justify-start gap-4">
                                                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                    </svg>
                                                </div>
                                                <span className="pt-1 text-left text-sm font-medium leading-snug text-gray-700">
                                                    {t('emptyResultHints.bullet1')}
                                                </span>
                                            </li>
                                            <li className="flex w-full items-start justify-start gap-4">
                                                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                                                    </svg>
                                                </div>
                                                <span className="pt-1 text-left text-sm font-medium leading-snug text-gray-700">
                                                    {t('emptyResultHints.bullet2')}
                                                </span>
                                            </li>
                                            <li className="flex w-full items-start justify-start gap-4">
                                                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                                    </svg>
                                                </div>
                                                <span className="pt-1 text-left text-sm font-medium leading-snug text-gray-700">
                                                    {t('emptyResultHints.bullet3')}
                                                </span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            ) : (
                                <div className="relative">
                                    <div
                                        className={
                                            analysisPlanUpsellModalVisible
                                                ? 'pointer-events-none select-none blur-[3px]'
                                                : ''
                                        }
                                    >
                                        <PanelContents
                                            componentMap={componentMap}
                                            panelsOverlayLoading={
                                                isFetchingMore ||
                                                (analysisFirstPaintDone && isWarmupLoading)
                                            }
                                            panelsOverlayLabel={t('clientData.fetchingMore')}
                                        />
                                    </div>
                                    {analysisPlanUpsellModalVisible && (
                                        <div
                                            className="absolute inset-0 z-20 flex items-start justify-center overflow-y-auto bg-white/55 px-4 pt-8 pb-16 backdrop-blur-sm sm:px-6 lg:px-8"
                                            role="dialog"
                                            aria-modal="true"
                                            aria-labelledby={
                                                scheduleDisplayLimitLocked
                                                    ? 'schedule-display-limit-modal-title'
                                                    : 'free-analysis-period-upsell-title'
                                            }
                                        >
                                            <div className="max-w-lg rounded-xl border border-gray-200 bg-white/95 p-6 text-center shadow-xl">
                                                {scheduleDisplayLimitLocked ? (
                                                    <>
                                                        <h2
                                                            id="schedule-display-limit-modal-title"
                                                            className="text-lg font-bold text-gray-900"
                                                        >
                                                            {t('scheduleLimit.title')}
                                                        </h2>
                                                        <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-gray-600">
                                                            {t(
                                                                userInfo?.isPaid
                                                                    ? 'scheduleLimit.bodyPaid'
                                                                    : 'scheduleLimit.bodyFree',
                                                                {
                                                                    max: displayScheduleLimitMax,
                                                                    paidMaxFormatted:
                                                                        basicPlanScheduleCapFormatted,
                                                                },
                                                            )}
                                                        </p>
                                                        <Link
                                                            to={buildPlansUrlWithTracking({
                                                                source: 'analysis_schedule_display_limit',
                                                            })}
                                                            className="mt-5 inline-flex items-center justify-center rounded-lg bg-kotomilThemaColor px-5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm hover:opacity-90"
                                                        >
                                                            {t('scheduleLimit.cta')}
                                                        </Link>
                                                    </>
                                                ) : (
                                                    <>
                                                        <h2
                                                            id="free-analysis-period-upsell-title"
                                                            className="text-lg font-bold text-gray-900"
                                                        >
                                                            {t('freePeriod.title')}
                                                        </h2>
                                                        <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-gray-600">
                                                            {t('freePeriod.body')}
                                                        </p>
                                                        <Link
                                                            to={buildPlansUrlWithTracking({
                                                                source: 'analysis_out_of_free_period_range',
                                                            })}
                                                            className="mt-5 inline-flex items-center justify-center rounded-lg bg-kotomilThemaColor px-5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm hover:opacity-90"
                                                        >
                                                            {t('freePeriod.cta')}
                                                        </Link>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            <FeedbackForm />
                        </div>
                    </MainContentsLayout>
                </PrivateLayout>
            )}
        </FirstTimeExperienceManager>
    );
}

export default Analysis;
